import React from 'react';
import { AlertTriangle, CheckCircle2, Clock, Loader2 } from 'lucide-react';
import { ScraperExecution, ScraperQueuedRun } from '../services/api';

const formatWait = (seconds: number) => seconds < 60 ? `${seconds}s` : `${Math.floor(seconds / 60)}m ${seconds % 60}s`;

const RunQueueBanner: React.FC<{
  queuedRun: ScraperQueuedRun | null;
  execution: ScraperExecution | null;
  workerMode: 'local' | 'cron';
  retryWorker: () => void;
  retrying: boolean;
}> = ({ queuedRun, execution, workerMode, retryWorker, retrying }) => {
  let title = 'Scrapers idle';
  let detail = 'No run is queued or active. Choose Run scrape to check availability.';
  let icon = <Clock className="w-5 h-5 text-ink-400" />;
  let tone = 'border-ink-200';
  let delayed = false;
  if (queuedRun) {
    const local = (queuedRun.worker_mode || workerMode) === 'local';
    delayed = queuedRun.waiting_seconds > (local ? 30 : 150);
    title = `Scrape queued · party of ${queuedRun.party_size ?? '?'}${delayed ? ' · pickup delayed' : ''}`;
    detail = `Waiting ${formatWait(queuedRun.waiting_seconds)}. ` + (local
      ? delayed ? 'The local worker has not picked this up. Retry pickup below; if it still waits, check logs/scraper-manual.log.' : 'The local worker will pick this up when the scraper lock is free.'
      : delayed ? 'The server has not picked this up yet. Another run may hold the lock, or the cron worker needs attention.' : 'The server checks the queue every 2 minutes and starts it when no other scrape is running.');
    icon = delayed ? <AlertTriangle className="w-5 h-5 text-amber-600" /> : <Loader2 className="w-5 h-5 text-brand-500 animate-spin" />;
    tone = delayed ? 'border-amber-200' : 'border-brand-200';
  } else if (execution) {
    const failed = execution.status === 'failed';
    const warnings = execution.status === 'completed_with_errors';
    title = failed ? 'Scrape failed' : warnings ? 'Scrape completed with errors' : 'Scrape completed';
    detail = `${execution.venues_done}/${execution.venues_total} venues checked · ${execution.slots} slots found · ${execution.errors} errors. ${execution.message}`;
    tone = failed ? 'border-rose-200' : warnings ? 'border-amber-200' : 'border-brand-200';
    icon = failed || warnings ? <AlertTriangle className="w-5 h-5 text-amber-600" /> : <CheckCircle2 className="w-5 h-5 text-brand-600" />;
  }
  return <div role="status" aria-live="polite" className={`bg-surface rounded-xl shadow-sm border ${tone} p-5 mb-6 flex items-start gap-3`}>
    <span className="shrink-0 mt-0.5">{icon}</span>
    <div className="min-w-0">
      <p className="text-sm font-medium text-ink-900">{title}</p>
      <p className="text-sm text-ink-600 mt-1 tabular-nums break-words">{detail}</p>
      {!queuedRun && execution?.finished_at && <p className="text-xs text-ink-500 mt-2">Finished {new Date(execution.finished_at).toLocaleString()}</p>}
      {queuedRun && workerMode === 'local' && delayed && <button type="button" disabled={retrying} onClick={retryWorker} className="mt-3 rounded-lg border border-brand-200 px-4 py-2 text-sm text-brand-700 disabled:opacity-50">{retrying ? 'Starting worker…' : 'Retry pickup'}</button>}
    </div>
  </div>;
};

export default RunQueueBanner;
